/**
 * 支付模块相关类型定义
 */

import type { PaginationData } from '../request'

// ==================== 枚举定义 ====================

/**
 * 支付方式枚举
 */
export enum PaymentMethodEnum {
    WECHAT = 'wechat',  // 微信支付
    ALIPAY = 'alipay',  // 支付宝
    FUBEI = 'fubei',    // 富呗支付
    WALLET = 'wallet',  // 余额支付
    MANUAL = 'manual'   // 人工确认
}

/**
 * 订单状态枚举
 */
export enum PaymentOrderStatusEnum {
    PENDING = 'PENDING',                // 待支付
    PAID = 'PAID',                     // 已支付-租赁中
    BUYER_COMPLETED = 'BUYER_COMPLETED', // 租客确认完成
    SELLER_CONFIRMED = 'SELLER_CONFIRMED', // 卖家确认归还
    COMPLETED = 'COMPLETED',           // 最终完成
    CANCELLED = 'CANCELLED',           // 已取消
    REFUNDING = 'REFUNDING',           // 退款中
    REFUNDED = 'REFUNDED'              // 已退款
}

/**
 * 订单状态显示文本映射
 */
export const PAYMENT_ORDER_STATUS_LABELS: Record<PaymentOrderStatusEnum, string> = {
    [PaymentOrderStatusEnum.PENDING]: '待租客付款',
    [PaymentOrderStatusEnum.PAID]: '租赁中',
    [PaymentOrderStatusEnum.BUYER_COMPLETED]: '待号主确认消耗物',
    [PaymentOrderStatusEnum.SELLER_CONFIRMED]: '待租客核对消耗物',
    [PaymentOrderStatusEnum.COMPLETED]: '订单完成',
    [PaymentOrderStatusEnum.CANCELLED]: '已取消',
    [PaymentOrderStatusEnum.REFUNDING]: '退款中',
    [PaymentOrderStatusEnum.REFUNDED]: '已退款'
}

/**
 * 支付方式显示文本映射
 */
export const PAYMENT_METHOD_LABELS: Record<PaymentMethodEnum, string> = {
    [PaymentMethodEnum.WECHAT]: '微信支付',
    [PaymentMethodEnum.WALLET]: '余额支付',
    [PaymentMethodEnum.ALIPAY]: '支付宝',
    [PaymentMethodEnum.FUBEI]: '富呗支付',
    [PaymentMethodEnum.MANUAL]: '人工确认'
}

/**
 * 交易类型枚举
 */
export enum TradeTypeEnum {
    JSAPI = 'JSAPI',   // JSAPI支付
    NATIVE = 'NATIVE', // Native支付
    APP = 'APP',       // APP支付
    MWEB = 'MWEB',     // H5支付
    PAGE = 'page',     // 电脑网站支付
    WAP = 'wap'        // 手机网站支付
}

// ==================== 数据模型 ====================

/**
 * 用户信息接口
 */
export interface UserInfo {
    id: number
    nickname: string
    phone?: string
    qq?: string
    wechat?: string
    is_member?: boolean
}

/**
 * 支付订单列表项
 * 用于订单列表页面显示精简信息
 */
export interface PaymentOrderListItem {
    /** 订单ID */
    id: number
    /** 商户订单号 */
    order_no: string
    /** 订单状态 */
    status: PaymentOrderStatusEnum
    /** 当前登录用户在订单中的身份 */
    viewer_role?: 'buyer' | 'seller' | 'both' | 'admin' | null
    /** 支付方式 */
    payment_method: PaymentMethodEnum
    /** 订单金额(分) */
    total_amount: number
    /** 租金金额(分) */
    rental_amount: number
    /** 押金金额(分) */
    deposit_amount: number
    /** 是否使用会员免押 */
    used_member_deposit_waiver?: boolean
    /** 会员免押金额(分) */
    member_deposit_waiver_amount?: number
    /** 佣金金额(分) */
    commission_amount: number
    /** 消耗费金额(分) - 根据消耗物明细计算的总费用 */
    consumption_amount: number
    /** 支付截止时间 */
    payment_deadline?: string | null
    /** 租赁截止时间 */
    rental_deadline?: string | null
    /** 支付时间 */
    pay_at?: string | null
    /** 创建时间 */
    created_at: string
    /** 更新时间 */
    updated_at: string
    /** 倒计时秒数 */
    countdown_seconds: string
    /** 是否超时 */
    is_timeout: string
    /** 是否可以取消 */
    can_cancel: string
    /** 是否可以确认 */
    can_confirm: string
    /** 租赁天数 */
    rental_days: string
    /** 买家信息 */
    buyer_info: string
    /** 账号信息 */
    account_info: string
    /** 卖家信息 */
    seller_info: string
    /** 租客确认完成时间 */
    buyer_completed_at?: string | null
    /** 卖家确认归还时间 */
    seller_confirmed_at?: string | null
    /** 最终完成时间 */
    final_completed_at?: string | null
    /** 买家对象 (管理员视图) */
    buyer?: UserInfo
    /** 卖家对象 (管理员视图) */
    seller?: UserInfo
    /** 账号ID (管理员视图) */
    account_id?: number
    /** 账号快照 */
    account_snapshot?: string | object
    /** 群二维码链接（从账号冗余到订单） */
    group_qr_code?: string | null
    group_qr_image?: string | null
}

/**
 * 支付订单详情
 * 包含订单的完整信息，用于订单详情页面显示
 */
export interface PaymentOrderDetail {
    /** 订单ID */
    id: number
    /** 当前登录用户在订单中的身份 */
    viewer_role?: 'buyer' | 'seller' | 'both' | 'admin' | null
    /** 倒计时秒数 */
    countdown_seconds: string
    /** 是否超时 */
    is_timeout: string
    /** 租赁天数 */
    rental_days: string
    /** 买家信息 */
    buyer_info: string
    /** 账号信息（兼容旧字段） */
    account_info: string | object
    /** 账号详情（根据权限返回，可能包含敏感信息） */
    account?: {
        id: number
        title?: string
        status: string
        login_method?: 'qq' | 'wechat' | 'steam' | string
        login_sub_method?: 'password' | 'qrcode' | string | null
        is_real_name_owner?: boolean
        qq_account?: string | null
        qq_password?: string | null
        steam_account?: string | null
        /** 群二维码链接（dataURL 或外链） */
        group_qr_code?: string | null
        group_qr_image?: string | null
        screenshots?: string[]
    }
    /** 账号快照 */
    account_snapshot: string | object
    /** 群二维码链接（从账号冗余到订单） */
    group_qr_code?: string | null
    group_qr_image?: string | null
    /** 商户订单号 */
    order_no: string
    /** 订单金额(分) */
    total_amount: number
    /** 订单状态 */
    status: PaymentOrderStatusEnum
    /** 租金金额(分) */
    rental_amount: number
    /** 押金金额(分) */
    deposit_amount: number
    /** 是否使用会员免押 */
    used_member_deposit_waiver?: boolean
    /** 会员免押金额(分) */
    member_deposit_waiver_amount?: number
    /** 佣金金额(分) */
    commission_amount: number
    /** 消耗物明细 */
    consumption_details?: unknown
    /** 消耗费金额(分) - 根据消耗物明细计算的总费用 */
    consumption_amount: number
    /** 租客确认完成时间 */
    buyer_completed_at?: string | null
    /** 卖家确认归还时间 */
    seller_confirmed_at?: string | null
    /** 最终完成时间 */
    final_completed_at?: string | null
    /** 卖家备注(消耗说明) */
    seller_notes?: string
    /** 支付方式 */
    payment_method: PaymentMethodEnum
    /** 交易类型 */
    trade_type?: TradeTypeEnum | null
    /** 第三方交易号 */
    transaction_id?: string | null
    /** 微信openid */
    openid?: string | null
    /** 预支付ID(微信) */
    prepay_id?: string | null
    /** 二维码链接(微信Native) */
    code_url?: string | null
    /** 支付宝买家ID */
    alipay_buyer_id?: string | null
    /** 支付截止时间 */
    payment_deadline?: string | null
    /** 租赁截止时间 */
    rental_deadline?: string | null
    /** 支付时间 */
    pay_at?: string | null
    /** 创建时间 */
    created_at: string
    /** 更新时间 */
    updated_at: string
    /** 是否已删除 */
    is_deleted: boolean
    /** 删除时间 */
    deleted_at?: string | null
    /** 买家（后端可能返回对象或ID，做兼容） */
    buyer?: UserInfo | number
    /** 卖家（后端可能返回对象或ID，做兼容） */
    seller?: UserInfo | number
    /** 游戏账号ID */
    game_account: number
    /** 删除操作者ID */
    deleted_by?: number | null
    /** 管理员调整快照（折扣信息） */
    admin_adjustment_snapshot?: {
        discount_percent?: number | null       // 折扣百分比，如80表示8折
        deposit_reduction?: number             // 押金减免金额(分)
        original_deposit?: number              // 原始押金(分)
        original_rental_price?: number         // 原始租金(分)
        original_commission?: number           // 原始佣金(分)
        modified_at?: string | null
        modified_by_id?: number | null
        modified_by_name?: string | null
        reason?: string | null
    } | null
}

// ==================== 请求参数类型 ====================

/**
 * 支付订单列表请求参数
 */
export interface PaymentOrderListRequest {
    /** 页码 */
    page?: number
    /** 每页大小 */
    size?: number
    /** 排序字段 */
    ordering?: '-created_at' | '-pay_at' | '-total_amount' | 'created_at' | 'pay_at' | 'total_amount'
    /** 支付方式筛选 */
    payment_method?: PaymentMethodEnum
    /** 搜索关键词（订单号、用户昵称、手机号） */
    search?: string
    /** 订单状态筛选 */
    status?: PaymentOrderStatusEnum | 'ONGOING'
    /** 按游戏账号ID筛选 */
    game_account_id?: number
}

/**
 * 我的销售订单列表请求参数
 */
export interface PaymentOrderMySalesRequest {
    /** 页码 */
    page?: number
    /** 每页大小 */
    size?: number
    /** 排序字段 */
    ordering?: '-created_at' | '-pay_at' | '-total_amount' | 'created_at' | 'pay_at' | 'total_amount'
    /** 支付方式筛选 */
    payment_method?: PaymentMethodEnum
    /** 搜索关键词（订单号、买家昵称、手机号） */
    search?: string
    /** 订单状态筛选（仅支持有效销售状态） */
    status?: 'BUYER_COMPLETED' | 'COMPLETED' | 'PAID' | 'REFUNDED' | 'REFUNDING' | 'SELLER_CONFIRMED'
    /** 按游戏账号ID筛选（后端已支持） */
    game_account_id?: number
}

/**
 * 创建租赁订单请求参数
 */
export interface PaymentCreateRentalRequest {
    /** 游戏账号ID，必填参数 */
    game_account_id: number
    rental_days?: number
    /** 会员是否使用当天一次免押额度 */
    use_member_deposit_waiver?: boolean
    /** 支付方式，默认为人工确认 */
    payment_method?: PaymentMethodEnum
}

/**
 * 租客确认完成租赁请求参数
 */
export interface PaymentBuyerCompleteRequest {
    /** 租客评分(1-5分) */
    rating?: number
    /** 租客反馈 */
    feedback?: string
}

/**
 * 消耗物项目
 */
export interface ConsumptionItem {
    /** 规则ID */
    rule_id: number
    /** 使用数量 */
    used_count: number
}

/**
 * 卖家确认归还请求参数
 */
export interface PaymentSellerConfirmRequest {
    /** 游戏账号ID（可选：提供则按账号自动定位订单，否则按URL中的订单ID处理） */
    game_account_id?: number
    /** 消耗物项目列表 */
    consumption_items?: ConsumptionItem[]
    /** 自定义消耗费金额(分) */
    custom_amount?: number
    /** 自定义消耗费备注 */
    custom_notes?: string
}

/**
 * 用户取消订单请求参数
 */
export interface PaymentUserCancelOrderRequest {
    /** 取消原因 */
    reason?: string
}

/**
 * 用户取消退款申请请求参数
 */
export interface PaymentCancelRefundRequest {
    /** 取消原因 */
    reason?: string
}

/**
 * 最终确认请求参数
 */
export interface PaymentFinalConfirmRequest {
    /** 最终确认备注 */
    confirm_notes?: string
}

// ==================== 响应类型 ====================

/**
 * 支付订单列表响应
 */
export type PaymentOrderListResponse = PaginationData<PaymentOrderListItem>

/**
 * 我的销售订单列表响应
 */
export type PaymentOrderMySalesResponse = PaginationData<PaymentOrderListItem>

/**
 * 支付订单详情响应
 */
export type PaymentOrderDetailResponse = PaymentOrderDetail

/**
 * 创建租赁订单响应
 */
export type PaymentCreateRentalResponse = PaymentOrderDetail

/**
 * 租客确认完成租赁响应
 */
export type PaymentBuyerCompleteResponse = {
    feedback?: string
    rating?: number
}

/**
 * 卖家确认归还响应
 */
export type PaymentSellerConfirmResponse = {
    consumption_details: unknown
    deducted_amount: number
}

/**
 * 用户取消订单响应
 */
export type PaymentUserCancelOrderResponse = {
    reason?: string
}

/**
 * 最终确认响应
 */
export type PaymentFinalConfirmResponse = {
    confirm_notes?: string
}

/**
 * 用户取消退款申请响应
 */
export type PaymentCancelRefundResponse = {
    refund_id: number
    message: string
}

/**
 * 检查订单支付状态响应
 */
export interface PaymentCheckStatusResponse {
    /** 订单ID */
    order_id: number
    /** 商户订单号 */
    order_no: string
    /** 订单状态 */
    status: PaymentOrderStatusEnum
    /** 状态显示文本 */
    status_display: string
    /** 是否已支付 */
    is_paid: boolean
    /** 是否超时 */
    is_timeout: boolean
    /** 倒计时剩余秒数 */
    countdown_seconds: number
    /** 支付截止时间 */
    payment_deadline?: string | null
    /** 支付时间 */
    pay_at?: string | null
    /** 是否可以取消 */
    can_cancel: boolean
    /** 更新时间 */
    updated_at: string
    /** 总超时时间（秒） */
    total_timeout_seconds: number
}

// ==================== 订单统计相关类型 ====================

/**
 * 订单统计数据
 */
export interface OrderStatistics {
    /** 待支付订单数量 */
    pending_orders: number
    /** 进行中订单数量 */
    paid_orders: number
    /** 待确认订单数量 */
    pending_confirm_orders: number
    /** 已完成订单数量 */
    completed_orders: number
    /** 已取消订单数量 */
    cancelled_orders: number
    /** 退款中订单数量 */
    refunding_orders: number
    /** 已退款订单数量 */
    refunded_orders: number
    /** 总订单数量 */
    total_orders: number
}

/**
 * 订单统计响应
 */
export type OrderStatisticsResponse = OrderStatistics

// ==================== 支付宝支付相关类型 ====================

/**
 * 支付宝支付响应（最小返回集）
 * - PC端：仅返回 qr_image_base64
 * - 移动端：仅返回 redirect_url
 */
export interface AlipayPaymentResponse {
    /** 二维码PNG的Base64（仅PC端） */
    qr_image_base64?: string
    /** 支付宝跳转链接（仅移动端） */
    redirect_url?: string
    /** 完整签名支付链接（PC/移动端均可返回） */
    payment_url?: string
    /** 支付二维码内容（预创建返回）或中转URL */
    qr_code?: string
    /** 过期时间（ISO字符串） */
    expires_at?: string
}

export interface FubeiPaymentRequest {
    pay_type?: 'alipay' | 'wxpay' | 'unionpay' | 'aggregate' | string
    pay_way?: '02' | '03' | '04' | '05' | string
    user_id?: string
    sub_appid?: string
    return_url?: string
}

export interface FubeiPaymentResponse {
    payment_url?: string
    redirect_url?: string
    qr_code?: string
    prepay_id?: string
    sign_package?: Record<string, unknown>
    union_pay_url?: string
    expires_at?: string
    raw_data?: Record<string, unknown>
}

export interface FubeiAuthUrlRequest {
    pay_type?: 'alipay'
    pay_way?: '02' | '03' | '04' | '05'
    sub_appid?: string
    return_url?: string
}

export interface FubeiAuthUrlResponse {
    auth_url: string
}
/**
 * 管理员强制完成订单请求参数
 */
export interface AdminForceCompleteRequest {
    /** 消耗物项目列表 */
    consumption_items?: ConsumptionItem[]
    /** 自定义消耗费金额(分) */
    custom_amount?: number
    /** 自定义消耗费备注 */
    custom_notes?: string
    /** 管理员操作备注 */
    notes?: string
}
